import { motion } from "framer-motion";

export default function EtOriginStory() {

  // First Created - 22 Nov 2025
  // Author - Atul Pandey
  // Purpose — Calm storytelling
  // Updated - 22 Nov 2025 - Cinematic Storytelling Section — Warm, Atmospheric, Purpose-Native

  return (

    <section className="relative overflow-hidden py-32 bg-black text-white">

      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-950 to-black"></div>
      

      {/* Soft vertical glow */}
      <div
        className="
          absolute left-1/2 top-0 -translate-x-1/2
          w-[600px] h-full
          bg-gradient-to-b from-orange-600/20 via-orange-500/10 to-transparent
          blur-[120px] 
          opacity-90
        "
      ></div>

      {/* Secondary radial glow behind text */}
      <div
        className="
          absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
          w-[700px] h-[700px]
          bg-orange-500/10 rounded-full blur-[160px]
        "
      ></div>

      {/* Subtle particles */}
      <div className="pointer-events-none absolute inset-0">
        {[...Array(120)].map((_, i) => (
          <div
            key={i}
            className="absolute w-[4px] h-[4px] bg-orange-300/20 rounded-full animate-origin-glow"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${6 + Math.random() * 4}s`,
            }}
          ></div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-20 max-w-4xl mx-auto px-6 text-center">

        {/* Section Title */}
        <motion.h2
          className="text-3xl md:text-4xl font-bold text-orange-500 mb-14"
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          The Origin
        </motion.h2>

        {/* Paragraphs */}
        <motion.div
          className="space-y-6 text-lg text-gray-300 leading-relaxed font-extralight"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{
            hidden: {},
            visible: {
              transition: { staggerChildren: 0.25 }
            }
          }}
        >
          {/* p1 */}
          <motion.p
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 1 } }
            }}
          >
            Eternity began with a realization. Growth is not the problem. It is the noise around it that overwhelms leaders. 
            The pace. The pressure. The fragmentation. The absence of a place to think with 
            <span className="text-orange-600 font-light"> clarity</span>.
          </motion.p>

          {/* p2 */}
          <motion.p
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 1 } }
            }}
          >
            Eternity was created to bring that clarity back. To give leaders a way to 
            <span className="text-orange-600 font-light"> understand</span> themselves. 
            To design their 
            <span className="text-orange-600 font-light"> purpose</span>. 
            To build a 
            <span className="text-orange-600 font-light"> rhythm</span> they can rely on.
          </motion.p>

          {/* p3 – refined “induce deeper thinking” */}
          <motion.p
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 1 } }
            }}
          >
            And then Trinity emerged. As the companion that brings this clarity into daily practice. 
            A way to 
            <span className="text-orange-600 font-light"> induce deeper thinking</span>. 
            To 
            <span className="text-orange-600 font-light"> imprint</span> what matters. 
            To 
            <span className="text-orange-600 font-light"> illuminate</span> progress every day.
          </motion.p>

          {/* p4 */}
          <motion.p
            className="text-orange-400 font-light mt-4"
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 1 } }
            }}
          >
            This is where the journey began.
          </motion.p>
        </motion.div>
      </div>

      {/* Motion Keyframes */}
      <style>{`
        @keyframes originGlow {
          0% { transform: translateY(0) scale(0.8); opacity: 0.15; }
          50% { transform: translateY(-25px) scale(1); opacity: 0.35; }
          100% { transform: translateY(0) scale(0.8); opacity: 0.15; }
        }
        .animate-origin-glow {
          animation: originGlow linear infinite;
        }
      `}</style>
    </section>
    // <section className="relative overflow-hidden py-28 bg-black text-white">

    //   {/* --- 1. Background Gradient Layer (Warm Ember) --- */}
    //   <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900 to-orange-900/20"></div>

    //   {/* --- 2. Subtle Texture Overlay --- */}
    //   <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>

    //   {/* --- 3. Horizontal Glow Lines (Narrative Divider) --- */}
    //   <div className="pointer-events-none absolute top-0 left-0 w-full flex justify-center">
    //     <div className="w-3/4 h-[2px] bg-gradient-to-r from-transparent via-orange-600/30 to-transparent"></div>
    //   </div>
    //   <div className="pointer-events-none absolute bottom-0 left-0 w-full flex justify-center">
    //     <div className="w-2/3 h-[2px] bg-gradient-to-r from-transparent via-orange-500/20 to-transparent"></div>
    //   </div>

    //   {/* --- 4. Ambient Particle Field (lighter, smaller) --- */}
    //   <div className="pointer-events-none absolute inset-0">
    //     {[...Array(300)].map((_, i) => (
    //       <div
    //         key={i}
    //         className="absolute w-[3px] h-[3px] bg-orange-400/25 rounded-full animate-particle-float"
    //         style={{
    //           top: `${Math.random() * 100}%`,
    //           left: `${Math.random() * 100}%`,
    //           animationDelay: `${Math.random() * 2}s`,
    //           animationDuration: `${5 + Math.random() * 2}s`,
    //         }}
    //       ></div>
    //     ))}
    //   </div>

    //   {/* --- 5. Content Container --- */}
    //   <div className="relative z-20 max-w-5xl mx-auto px-6">

    //     {/* Title */}
    //     <motion.h2
    //       className="text-3xl md:text-4xl font-bold text-orange-500 mb-8 text-center md:text-left"
    //       initial={{ opacity: 0, y: 20 }}
    //       whileInView={{ opacity: 1, y: 0 }}
    //       viewport={{ once: true }}
    //       transition={{ duration: 0.8 }}
    //     >
    //       Our Origin
    //     </motion.h2>

    //     {/* Narrative Paragraphs */}
    //     <motion.div
    //       className="space-y-6 text-gray-300 text-lg leading-relaxed font-extralight"
    //       initial="hidden"
    //       whileInView="visible"
    //       viewport={{ once: true }}
    //       variants={{
    //         hidden: { opacity: 0, y: 20 },
    //         visible: {
    //           opacity: 1,
    //           y: 0,
    //           transition: {
    //             duration: 1.1,
    //             staggerChildren: 0.25,
    //           },
    //         },
    //       }}
    //     >
    //       <motion.p variants={{}}>
    //         Eternity was born from three decades of lived experience in the tech
    //         services world — in boardrooms, delivery floors, client conversations,
    //         and leadership trenches.
    //       </motion.p>

    //       <motion.p variants={{}}>
    //         Over time, a clear pattern emerged: leaders were not limited by
    //         ambition or capability, but by a lack of clarity, narrative cohesion,
    //         and consistent execution. Organizations were busy, but not aligned.
    //         Leaders were committed, but overwhelmed.
    //       </motion.p>

    //       <motion.p variants={{}}>
    //         And underneath the operational noise were deeper bottlenecks —
    //         mindset gaps, habit gaps, reflection gaps — the invisible factors
    //         shaping performance far more than any spreadsheet or dashboard.
    //       </motion.p>

    //       <motion.p variants={{}}>
    //         Eternity was created to help leaders see these truths, articulate
    //         their purpose, and reimagine how they think, plan, and execute.
    //         Trinity emerged as the platform that transforms those insights into a
    //         living, purpose-native operating system.
    //       </motion.p>
    //     </motion.div>
    //   </div>

    //   {/* --- Motion Keyframes --- */}
    //   <style>{`
    //     @keyframes particle-float {
    //       0% { transform: translateY(0) scale(0.8); opacity: 0.15; }
    //       50% { transform: translateY(-25px) scale(1); opacity: 0.5; }
    //       100% { transform: translateY(0) scale(0.8); opacity: 0.15; }
    //     }
    //     .animate-particle-float {
    //       animation: particle-float linear infinite;
    //     }
    //   `}</style>
    // </section>
  );
}